import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import ChatArea from '../components/ChatArea';
import { useAuth } from '../contexts/AuthContext';
import Eraser from '../icons/eraser.png';
import Logout from '../icons/logout.png';
import NightModeIcon from '../icons/moon.svg';
import DayModeIcon from '../icons/sun-2-svgrepo-com.svg';
import Logo from '../icons/logo.png';
import '../App.css';
import { ThemeContext } from '../ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { introductionText, introductionTextMobile, API_BASE_URL, BASE_URL } from '../config';
import { getGuestId } from '../components/ChatArea';
import FeedbackModal from '../components/FeedbackModal';
import ReferralModal from '../components/ReferralModal';
import CompleteProfileModal from '../components/CompleteProfileModal';
import ReferALeadModal from '../components/ReferALeadModal';

function getInitials(name) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const ChatPage = () => {
  const { user, logout, authFetch } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const mobileFromUrl = new URLSearchParams(search).get('m');
  const userId = user?.email || getGuestId();
  const isGuest = userId.startsWith('guest');
  const guestName = localStorage.getItem('guestName');
  const displayName = user?.name || guestName;
  const initials = getInitials(displayName);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedExample, setSelectedExample] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showReferALeadModal, setShowReferALeadModal] = useState(false);
  const [showCompleteProfileModal, setShowCompleteProfileModal] = useState(false);

  const popoverRef = useRef(null);
  const { theme, toggleTheme } = useContext(ThemeContext);

  const handleLogout = () => {
    logout();
    window.location.href = `${BASE_URL}/login.html`;
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setSidebarOpen(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const isMobileMissing = !user?.mobile_number || user.mobile_number === 'null' || user.mobile_number === '';
    if (user && isMobileMissing && !isGuest && !mobileFromUrl) {
      const timer = setTimeout(() => {
        setShowCompleteProfileModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user, isGuest]);

  // === on‐mount: initialize guest session with mobileFromUrl === this is for url with m=<mobile number>
  useEffect(() => {
    if (mobileFromUrl && isGuest) {
      const guestID = getGuestId();
      // register on backend
      authFetch(`${API_BASE_URL}/api/guest/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_id: guestID, name: "Guest", mobile: mobileFromUrl })
      }).catch(console.error);
    }
  }, [mobileFromUrl, isGuest, authFetch]);

  const togglePopover = () => setOpen((prev) => !prev);
  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);

  const handleClearChat = useCallback(async () => {
    if (user?.email && !isGuest) {
      try {
        await authFetch(`${API_BASE_URL}/api/clear-chat-history?user_id=${user.email}`, {
          method: 'DELETE',
        });
      } catch (err) {
        console.error('Error clearing chat history on backend:', err);
      }
    } else if (isGuest) {
      localStorage.removeItem('user');
    }
    localStorage.removeItem(`chatHistory_${user?.email || getGuestId()}`);
    setChatHistory([{ role: 'system', text: isMobile ? introductionTextMobile : introductionText }]);
    setConfirmOpen(false);
  }, [user, isGuest, authFetch, isMobile]);

  const handleSelectExample = useCallback((example) => {
    if (example === 'open_refer_modal') {
      setShowReferALeadModal(true);
    } else {
      setSelectedExample(example);
    }
  }, []);
  const handleClearSelectedExample = () => setSelectedExample(null);

  const handleProfileCompletion = (mobile) => {
    const updatedUser = { ...user, mobile_number: mobile };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setShowCompleteProfileModal(false);
  };

  return (
    <div className="app-container">
      {/* Navbar */}
      <div className="top-navbar">
        <div className="navbar-left">
          {isMobile && (
            <button className="hamburger-btn" onClick={toggleSidebar}>
              &#9776;
            </button>
          )}
          <div className="navbar-title">
            <a href="https://realtyseek.ai">
              <img src={Logo} alt="Logo" />
            </a>
          </div>
        </div>

        <div className="navbar-right">
          <img
            src={theme === 'dark' ? DayModeIcon : NightModeIcon}
            alt="Toggle theme"
            width="22px"
            height="22px"
            onClick={toggleTheme}
            style={{ cursor: 'pointer' }}
          />
          <div className="u-user-info" onClick={togglePopover}>
            {initials && <span className="user-initials">{initials}</span>}
          </div>
        </div>

        {confirmOpen && (
          <div className="confirmation-popover">
            <p>Are you sure you want to clear your chat?</p>
            <div className="confirmation-buttons">
              <button className="btn confirm" onClick={handleClearChat}>OK</button>
              <button className="btn cancel" onClick={() => setConfirmOpen(false)}>Cancel</button>
            </div>
          </div>
        )}

        {open && (
          <div className="popover" ref={popoverRef}>
            <div className="popover-content" onClick={() => {
              setOpen(false);
              setConfirmOpen(true);
            }}>
              <img src={Eraser} alt="Clear Chat" width="20px" height="20px" />
              <p className="popover-content-clear">Clear Chat</p>
            </div>

            {isGuest ? (
              <div className="popover-content" onClick={() => {
                setOpen(false);
                window.location.href = "/login.html";
              }}>
                <i className="bi bi-box-arrow-in-right" style={{ fontSize: "20px" }}></i>
                <p className="popover-content-clear">Login</p>
              </div>
            ) : (
              <>
                <div className="popover-content" onClick={() => {
                  setOpen(false);
                  window.open("/dashboard", "_blank");
                }}>
                  <i className="bi bi-speedometer2" style={{ fontSize: "20px" }}></i>
                  <p className="popover-content-clear">My Dashboard</p>
                </div>

                <div className="popover-content" onClick={() => {
                  setOpen(false);
                  setShowReferralModal(true);
                  gtag('event', 'open_invite_friend_modal', {
                    event_category: 'Referral',
                    event_label: user?.email || 'Guest User',
                  });
                }}>
                  <i className="bi bi-person-badge" style={{ fontSize: "20px" }}></i>
                  <p className="popover-content-clear">Invite Friends</p>
                </div>

                <div className="popover-content" onClick={() => {
                  setOpen(false);
                  setShowReferALeadModal(true);
                  gtag('event', 'open_refer_a_lead_modal', {
                    event_category: 'Referral',
                    event_label: user?.email || 'Guest User',
                  });
                }}>
                  <i className="bi bi-person-lines-fill" style={{ fontSize: "20px" }}></i>
                  <p className="popover-content-clear">Refer & Earn</p>
                </div>

                <div className="popover-content" onClick={() => {
                  setOpen(false);
                  handleLogout();
                }}>
                  <img src={Logout} alt="Logout" width="20px" height="20px" />
                  <p className="popover-content-clear">Logout</p>
                </div>
              </>
            )}

            <div className="popover-content" onClick={() => {
              setOpen(false);
              setFeedbackModalOpen(true);
            }}>
              <i className="bi bi-chat-dots" style={{ fontSize: "20px" }}></i>
              <p className="popover-content-clear">Feedback</p>
            </div>
          </div>
        )}
      </div>

      <div className={`sidebar-wrapper ${sidebarOpen ? 'open' : ''}`}>
        <Sidebar
          onClearChat={handleClearChat}
          onSelectExample={handleSelectExample}
          onCloseSidebar={toggleSidebar}
        />
      </div>

      {sidebarOpen && isMobile && <div className="sidebar-overlay" onClick={toggleSidebar}></div>}

      <div className="chat-area-wrapper">
        <ChatArea
          chatHistory={chatHistory}
          setChatHistory={setChatHistory}
          selectedExample={selectedExample}
          clearSelectedExample={handleClearSelectedExample}
          urlMobile={mobileFromUrl}          // pass it into ChatArea / your socket hook
        />
      </div>

      {/* Modals */}
      {feedbackModalOpen && (
        <FeedbackModal onClose={() => setFeedbackModalOpen(false)} />
      )}

      {showReferralModal && (
        <ReferralModal
          onClose={() => setShowReferralModal(false)}
          referralCode={user?.my_referral_code || "N/A"}
        />
      )}

      {showReferALeadModal && (
        <ReferALeadModal
          onClose={() => setShowReferALeadModal(false)}
          onSuccess={() => {}}
        />
      )}

      {showCompleteProfileModal && !isGuest && !mobileFromUrl && (
        <CompleteProfileModal
          userId={user?.user_id}
          pendingReferralCode={localStorage.getItem('pending_referral_code')}
          onSuccess={(mobile) => {
            handleProfileCompletion(mobile);
            localStorage.removeItem('pending_referral_code');
          }}
          onClose={() => setShowCompleteProfileModal(false)}
        />
      )}
    </div>
  );
};

export default ChatPage;
